import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useStore } from '../../../contexts/StoreContext';
import AuthStore from '../../../store/AuthStore';
import { CustomerDisplay } from '../../../store/LessonStore';
import { Routes } from '../../const/Routes';
import { colors } from '../../const/Styles';
import CustomerConditionFilterCell from './CustomerConditionFilterCell';
import CustomerNameFilterCell from './CustomerNameFilterCell';
import CustomerRegisterCell from './CustomerRegisterCell';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 200px;
    min-width: 200px;
    height: 100%;
    border: solid 4px ${colors.gray};
    border-top-style: none;
    border-bottom-style: none;
    box-sizing: border-box;
    overflow: auto;
`;

type Props = {
    onClick: (customers: CustomerDisplay[]) => void;
    filteredCounts?: Record<string, number>;
};
const CustomerNameFilter: React.FC<Props> = ({ onClick, filteredCounts }) => {
    const [selected, setSelected] = useState('ALL');
    
    const handleClick = useCallback(
        (filterName: string, customers: CustomerDisplay[]) => {
            setSelected(filterName);
            onClick(customers);
        },
        [onClick],
    );

    const { lessonStore } = useStore();
    const ALL = useMemo(() => {
        return function ALL() {
            return lessonStore.allCustomers.sort((a, b) =>
                a.name_sei_kana[0].localeCompare(b.name_sei_kana[0], 'ja'),
            );
        };
    }, [lessonStore.allCustomers]);

    const history = useHistory();
    const handleClickRegister = () => {
        history.push(Routes.CUSTOMER_REGISTER);
    };

    const handleChangeCondition = useCallback(async (checked: boolean) => {
        await lessonStore.fetchAllCustomers(AuthStore.tempoId, checked);

        setSelected(selected);
        onClick(selected === 'ALL' ? lessonStore.allCustomers : lessonStore.customersPerRow(selected));
    }, [selected, onClick, lessonStore])

    useEffect(() => {
        const fetchCustomers = async () => {
            await lessonStore.fetchAllCustomers(AuthStore.tempoId, lessonStore.isInclusionDeletedCustomer);
        }
        void fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 初期表示時にALLの顧客リストを親に渡す
    useEffect(() => {
        if (lessonStore.allCustomers.length > 0 && selected === 'ALL') {
            onClick(lessonStore.allCustomers);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonStore.allCustomers.length, selected]);

    return (
        <Wrapper>
            <CustomerConditionFilterCell onChange={handleChangeCondition} />
            <CustomerNameFilterCell
                filterName='ALL'
                customers={lessonStore.allCustomers}
                count={filteredCounts?.ALL}
                onClick={handleClick}
                isActive={ALL.name === selected}
                padding='30px'
            >
                ALL
            </CustomerNameFilterCell>
            {lessonStore.rows.map((row) => (
                <CustomerNameFilterCell
                    key={row}
                    filterName={row}
                    customers={lessonStore.customersPerRow(row)}
                    count={filteredCounts?.[row]}
                    onClick={handleClick}
                    isActive={row === selected}
                >
                    {row}
                </CustomerNameFilterCell>
            ))}
            <CustomerRegisterCell onClick={handleClickRegister} />
        </Wrapper>
    );
};

export default observer(CustomerNameFilter);
